import { Fragment, useCallback, useEffect,useLayoutEffect, useMemo, useState , useRef, type CSSProperties} from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Plus, FileText, Download, Home, Loader2, AlertCircle , StickyNote , Pencil, Copy, Trash2 } from 'lucide-react';
import { isAxiosError } from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card.tsx';
import { Button } from '../ui/button.tsx';
import { Badge, badgeVariants } from '../ui/badge.tsx';
import { Textarea } from '../ui/textarea.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.tsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import { Checkbox } from '../ui/checkbox.tsx';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog.tsx';
import { useSubprojectDetail } from '../../hook/useSubprojectDetail.ts';
import {
  Room,
  RoomItem,
  SubprojectExtra,
  RoomItemCreatePayload,
  RoomItemCreateFromCatalogPayload,
  SubprojectExtraCreatePayload,
  SubprojectExtraUpdatePayload,
  RoomItemUpdatePayload,
  SubprojectDetail as SubprojectDetailType,
  type SubprojectPdfOptionalColumn,
  type ProjectStatus,
} from '../../types/subprojects.ts';
import { formatProjectStatus } from '../../types/projects.ts';
import type { VariantProps } from 'class-variance-authority';
import { toast } from 'sonner';
import { useSubprojectsApi } from '../../services/subprojects.ts';
import { ItemFormDialog, type ItemFormValues } from '../subprojects/ItemFormDialog.tsx';
import {
  SubprojectCreateCatalogItemDialog,
  type SubprojectCreateCatalogItemForm,
} from '../subprojects/SubprojectCreateCatalogItemDialog.tsx';
import { SubprojectOrdersDialog } from '../subprojects/SubprojectOrdersDialog.tsx';
import {
  SubprojectDuplicateItemDialog,
  type SubprojectDuplicateItemForm,
} from '../subprojects/SubprojectDuplicateItemDialog.tsx';
import { Input } from '../ui/input.tsx';
import { useSuppliersApi } from '../../services/suppliers.ts';
import { useCatalogQuickSearch } from '../../services/catalog.ts';
import { useCategoriesApi } from '../../services/categories.ts';
import { useAppSettings } from '../../hook/useAppSettings.tsx';
import type { CatalogItem } from '../../types/catalog.ts';

interface SubprojectDetailProps {
  projectId: string;
  subprojectId: string;
  onBack: () => void;
  onSubprojectUpdated?: () => void;
  refreshVersion?: number;
}

const parseDecimal = (value: string | null | undefined) => {
  const numeric = value ? Number.parseFloat(String(value).replace(',', '.')) : 0;
  return Number.isFinite(numeric) ? numeric : 0;
};

const parseRequiredAmountInput = (value: string) => {
  const normalized = value.trim().replace(',', '.');
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const clampDiscount = (value: number) => Math.min(Math.max(value, 0), 100);
const normalizeItemRef = (value: string | null | undefined) =>
  String(value ?? '').trim().toUpperCase();

function useDebouncedValue<T>(value: T, delay: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

type CatalogSearchField = 'ref' | 'supplier' | 'description';
type CatalogAddMode = 'dialog' | 'inline' | null;
type DropdownPortalPosition = { top: number; left: number; width: number };

interface CatalogSearchInputProps {
  label: string;
  placeholder: string;
  value: string;
  field: CatalogSearchField;
  items: CatalogItem[];
  loading?: boolean;
  disabled?: boolean;
  renderInPortal?: boolean;
  onChange: (next: string) => void;
  onFocus: () => void;
  onSelect: (item: CatalogItem) => void;
}

type CategoryOption = { label: string; value: string };

function useDropdownPortalPosition(open: boolean) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<DropdownPortalPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const element = containerRef.current;
      if (!element) return;
      const rect = element.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  return { containerRef, position };
}


function CatalogSearchInput({
  label,
  placeholder,
  value,
  field,
  items,
  loading,
  disabled,
  renderInPortal = false,
  onChange,
  onFocus,
  onSelect,
}: CatalogSearchInputProps) {
  const [open, setOpen] = useState(false);
  const { containerRef, position } = useDropdownPortalPosition(open && renderInPortal);

  const filtered = useMemo(() => {
    const normalized = value.trim().toLowerCase();
    const base = normalized
      ? items.filter((item) => {
          const fieldValue = String(item[field as keyof CatalogItem] ?? '');
          return fieldValue.toLowerCase().includes(normalized);
        })
      : items;
    return base.slice(0, 10);
  }, [field, items, value]);

  return (
    <div ref={containerRef} className="space-y-1.5">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <div className="relative">
        <Input
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onFocus={() => {
            onFocus();
            setOpen(true);
          }}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            setTimeout(() => setOpen(false), 120);
          }}
          autoComplete="off"
        />
        {open && renderInPortal && position && typeof document !== 'undefined'
          ? createPortal(
          <div
            className="fixed z-[120] max-h-64 overflow-y-auto overflow-x-hidden rounded-md border bg-white shadow-lg"
            style={{ top: position.top, left: position.left, width: position.width }}
          >
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Buscando…</div>
            ) : null}
            {!loading && filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
            ) : (
              filtered.map((item) => (
                <button
                  type="button"
                  key={`${item.id}-${field}`}
                  className="w-full overflow-hidden px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onSelect(item);
                    setOpen(false);
                  }}
                >
                  <div className="truncate font-semibold text-gray-900">{item.ref}</div>
                  <div className="text-xs text-gray-600 truncate">
                    {item.supplier} • {item.description}
                  </div>
                </button>
              ))
            )}
          </div>,
          document.body,
        )
          : open ? (
          <div className="absolute z-[80] mt-1 max-h-64 w-full overflow-y-auto overflow-x-hidden rounded-md border bg-white shadow-lg">
            {loading ? (
              <div className="px-3 py-2 text-sm text-gray-500">Buscando…</div>
            ) : null}
            {!loading && filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
            ) : (
              filtered.map((item) => (
                <button
                  type="button"
                  key={`${item.id}-${field}`}
                  className="w-full overflow-hidden px-3 py-2 text-left text-sm hover:bg-gray-100"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onSelect(item);
                    setOpen(false);
                  }}
                >
                  <div className="truncate font-semibold text-gray-900">{item.ref}</div>
                  <div className="text-xs text-gray-600 truncate">
                    {item.supplier} • {item.description}
                  </div>
                </button>
              ))
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function CategorySearchInput({
  value,
  options,
  loading,
  disabled,
  renderInPortal = false,
  onChange,
  onSelect,
}: {
  value: string;
  options: CategoryOption[];
  loading?: boolean;
  disabled?: boolean;
  renderInPortal?: boolean;
  onChange: (next: string) => void;
  onSelect: (option: CategoryOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const { containerRef, position } = useDropdownPortalPosition(open && renderInPortal);

  const filtered = useMemo(() => {
    const normalized = value.trim().toLowerCase();
    const base = normalized
      ? options.filter((option) => option.label.toLowerCase().includes(normalized))
      : options;
    return base.slice(0, 20);
  }, [options, value]);

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onBlur={() => {
          setTimeout(() => setOpen(false), 120);
        }}
        autoComplete="off"
        placeholder="Busca y selecciona una categoría"
      />
      {open && renderInPortal && position && typeof document !== 'undefined'
        ? createPortal(
        <div
          className="fixed z-[120] max-h-64 overflow-y-auto overflow-x-hidden rounded-md border bg-white shadow-lg"
          style={{ top: position.top, left: position.left, width: position.width }}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
          ) : null}
          {!loading && filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
          ) : (
            filtered.map((option) => (
              <button
                type="button"
                key={option.value}
                className="w-full overflow-hidden px-3 py-2 text-left text-sm hover:bg-gray-100"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))
          )}
        </div>,
        document.body,
      )
        : open ? (
        <div className="absolute z-[80] mt-1 max-h-64 w-full overflow-y-auto overflow-x-hidden rounded-md border bg-white shadow-lg">
          {loading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Cargando…</div>
          ) : null}
          {!loading && filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">Sin resultados</div>
          ) : (
            filtered.map((option) => (
              <button
                type="button"
                key={option.value}
                className="w-full overflow-hidden px-3 py-2 text-left text-sm hover:bg-gray-100"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

const itemNetUnitPvp = (item: RoomItem) => {
  const base = parseDecimal(item.pvp);
  const discountFactor = clampDiscount(parseDecimal(item.discount)) / 100;
  return base * (1 - discountFactor);
};
const roomSubtotal = (room: Room) =>
  room.items.reduce((total, item) => total + itemNetUnitPvp(item) * item.quantity, 0);

const itemRealCostTotal = (item: RoomItem) => {
  const backendValue = parseDecimal(item.real_cost);
  if (item.real_cost_modi) {
    return backendValue;
  }
  const autoValue = parseDecimal(item.unit_cost) * item.quantity;
  return backendValue > 0 ? backendValue : autoValue;
};

const roomCost = (room: Room) =>
  room.items.reduce((total, item) => {
    const mounting = parseDecimal(item.mounting);
    return total + itemRealCostTotal(item) + mounting;
  }, 0);

const roomMargin = (room: Room) => {
  const totalCost = roomCost(room);
  const totalPvp = roomSubtotal(room);
  return totalPvp > 0 ? Math.round(((totalPvp - totalCost) / totalPvp) * 100) : 0;
};

const renderHighlightedValue = (value: React.ReactNode, shouldHighlight: boolean) =>
  shouldHighlight ? <span className={'rounded-full bg-orange-100 px-2 py-1 text-xs text-orange-800'}>{value}</span> : value;

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

const stateBadges: Record<RoomItem['state'], BadgeVariant> = {
  pendiente: 'secondary',
  pedido: 'inProgress',
  recibido: 'quoted',
  instalado: 'finished',
};

const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type ItemsByCategory = {
  category: string;
  items: RoomItem[];
};

type RoomWithGroupedItems = Room & {
  groupedItems: ItemsByCategory[];
};

type ItemStateFilter = 'all' | RoomItem['state'];

const ITEM_STATE_FILTERS: Array<{ label: string; value: ItemStateFilter }> = [
  { label: 'Todos los estados', value: 'all' },
  { label: 'Pendiente', value: 'pendiente' },
  { label: 'Pedido', value: 'pedido' },
  { label: 'Recibido', value: 'recibido' },
  { label: 'Instalado', value: 'instalado' },
];

const ROOM_ITEM_COLUMN_WIDTHS = {
  ref: '7%',
  supplier: '9%',
  description: '7%',
  quantity: '3%',
  pvpUnit: '4.6%',
  discount: '3.2%',
  pvpTotal: '4.6%',
  pvpIva: '4.6%',
  precioTarifa: '4.6%',
  desvioComercialPct: '3.2%',
  beneficioComercialPct: '3.2%',
  montajePct: '3.2%',
  unitCost: '4.6%',
  costTotal: '4.6%',
  mounting: '4.6%',
  marginTotal: '4.6%',
  realCost: '4.6%',
  desvioCoste: '4.6%',
  state: '5.5%',
  notes: '3.4%',
  actions: '6.3%',
} as const;
type RoomItemColumnKey = keyof typeof ROOM_ITEM_COLUMN_WIDTHS;
const ROOM_ITEM_COLUMN_ORDER: ReadonlyArray<RoomItemColumnKey> = [
  'ref',
  'supplier',
  'description',
  'quantity',
  'pvpUnit',
  'discount',
  'pvpTotal',
  'pvpIva',
  'precioTarifa',
  'desvioComercialPct',
  'beneficioComercialPct',
  'montajePct',
  'unitCost',
  'costTotal',
  'mounting',
  'marginTotal',
  'realCost',
  'desvioCoste',
  'state',
  'notes',
  'actions',
];
const ROOM_ITEM_SEPARATOR_START_KEYS: ReadonlyArray<RoomItemColumnKey> = [
  'pvpUnit',
  'precioTarifa',
  'unitCost',
  'realCost',
  'state',
];
const ROOM_ITEM_SEPARATOR_COLOR = 'rgba(229, 231, 235, 0.9)';
const ROOM_ITEM_SEPARATOR_LINE_WIDTH_PX = 1;
const parseColumnPercent = (value: string) => {
  const parsed = Number.parseFloat(value.replace('%', ''));
  return Number.isFinite(parsed) ? parsed : 0;
};
const buildRoomItemSeparatorOffsets = (
  columnOrder: ReadonlyArray<RoomItemColumnKey>,
  columnWidths: Record<RoomItemColumnKey, string>,
  separatorStartKeys: ReadonlyArray<RoomItemColumnKey>,
) => {
  const separatorStartSet = new Set<RoomItemColumnKey>(separatorStartKeys);
  let accumulatedWidth = 0;

  return columnOrder.reduce<number[]>((offsets, key) => {
    if (separatorStartSet.has(key)) {
      offsets.push(accumulatedWidth);
    }
    accumulatedWidth += parseColumnPercent(columnWidths[key]);
    return offsets;
  }, []);
};
const buildRoomItemSeparatorRowStyle = (offsets: number[]): CSSProperties => {
  if (offsets.length === 0) return {};

  const backgroundImage = offsets
    .map(
      (offset) =>
        `linear-gradient(to right, transparent ${offset}%, ${ROOM_ITEM_SEPARATOR_COLOR} ${offset}%, ${ROOM_ITEM_SEPARATOR_COLOR} calc(${offset}% + ${ROOM_ITEM_SEPARATOR_LINE_WIDTH_PX}px), transparent calc(${offset}% + ${ROOM_ITEM_SEPARATOR_LINE_WIDTH_PX}px))`,
    )
    .join(',');

  return { backgroundImage };
};
const ROOM_ITEM_SEPARATOR_OFFSETS = buildRoomItemSeparatorOffsets(
  ROOM_ITEM_COLUMN_ORDER,
  ROOM_ITEM_COLUMN_WIDTHS,
  ROOM_ITEM_SEPARATOR_START_KEYS,
);
const ROOM_ITEM_SEPARATOR_ROW_STYLE = buildRoomItemSeparatorRowStyle(ROOM_ITEM_SEPARATOR_OFFSETS);
const COMPACT_HEAD_CLASS = '!whitespace-normal break-words px-1 text-xs leading-tight';
const COMPACT_HEAD_CENTER_CLASS = `text-center ${COMPACT_HEAD_CLASS}`;
const TABLE_TOTAL_COLUMNS = 21;
const DEFAULT_CATEGORY_LABEL = 'Sin categoría';

const STATUS_BADGE_VARIANTS: Record<ProjectStatus, BadgeVariant> = {
  planificacion: 'planning',
  en_progreso: 'inProgress',
  presupuestado: 'quoted',
  finalizado: 'finished',
};

const PDF_FIXED_COLUMN_LABELS = ['Descripción', 'UD', 'PVP', 'PVP+IVA'];
const PDF_OPTIONAL_COLUMN_OPTIONS: Array<{ value: SubprojectPdfOptionalColumn; label: string }> = [
  { value: 'ref', label: 'Referencia' },
  { value: 'supplier', label: 'Proveedor' },
  { value: 'pvp_unit', label: 'PVP UD' },
  { value: 'discount', label: 'Desc. PVP' },
  { value: 'precio_tarifa', label: 'Precio tarifa' },
  { value: 'desvio_comercial_pct', label: 'Desvío comercial %' },
  { value: 'beneficio_comercial_pct', label: 'Beneficio comercial %' },
  { value: 'montaje_pct', label: 'Montaje %' },
  { value: 'unit_cost', label: 'Coste UD' },
  { value: 'cost_total', label: 'Coste total' },
  { value: 'mounting', label: 'Montaje' },
  { value: 'margin_total', label: 'Margen total' },
  { value: 'real_cost', label: 'Coste real' },
  { value: 'desvio_coste', label: 'Desvío coste' },
  { value: 'state', label: 'Estado' },
];
const PDF_OPTIONAL_COLUMN_VALUES = PDF_OPTIONAL_COLUMN_OPTIONS.map((option) => option.value);
const DEFAULT_PDF_OPTIONAL_COLUMNS = [...PDF_OPTIONAL_COLUMN_VALUES];

export function SubprojectDetail(props: SubprojectDetailProps) {
  const { projectId, subprojectId, onBack, onSubprojectUpdated } = props;
  const refreshVersion = props.refreshVersion;
  const {
    formatCurrency: formatMoney,
    formatPercent: formatPercentFromSettings,
  } = useAppSettings();
  const formatCurrency = (value: number) => formatMoney(value, { showZero: true });
  const formatPercent = (value: number | null | undefined) =>
    formatPercentFromSettings(value ?? 0, { maximumFractionDigits: 0 });
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [selectedState, setSelectedState] = useState<ItemStateFilter>('all');
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [createCatalogItemDialogOpen, setCreateCatalogItemDialogOpen] = useState(false);
  const [activeRoomId, setActiveRoomId] = useState<Room['id'] | null>(null);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [editingItem, setEditingItem] = useState<RoomItem | null>(null);
  const [draftRoomName, setDraftRoomName] = useState('');
  const [itemSubmitting, setItemSubmitting] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<RoomItem['id'] | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ roomId: Room['id']; item: RoomItem } | null>(null);
  const [itemToDuplicate, setItemToDuplicate] = useState<{ roomId: Room['id']; roomName: string; item: RoomItem } | null>(null);
  const [duplicatingItem, setDuplicatingItem] = useState(false);
  const scrollPosRef = useRef(0);

  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfExportDialogOpen, setPdfExportDialogOpen] = useState(false);
  const [isViewportAbove1920, setIsViewportAbove1920] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth > 1920;
  });
  const [selectedPdfOptionalColumns, setSelectedPdfOptionalColumns] = useState<
    SubprojectPdfOptionalColumn[]
  >(DEFAULT_PDF_OPTIONAL_COLUMNS);

  const { data, isLoading, error, refetch } = useSubprojectDetail(projectId, subprojectId);
  const {
    createRoom,
    createRoomItem,
    createRoomItemFromCatalog,
    createSubprojectExtra,
    updateSubprojectExtra,
    deleteSubprojectExtra,
    updateRoomItem,
    deleteRoomItem,
    duplicateRoomItem,
    updateRoom,
    downloadSubprojectExcel,
    downloadSubprojectPdf,
  } = useSubprojectsApi();
  const {
    data: supplierResults,
    loading: suppliersLoading,
  } = useSuppliersApi({}, { page: 1, page_size: 100 });
  const {
    results: catalogItems,
    loading: catalogLoading,
    search: searchCatalogItems,
    setResults: setCatalogResults,
  } = useCatalogQuickSearch();
  const { categories, loading: categoriesLoading } = useCategoriesApi({ active: true, limit: 300 });

  const subproject = data?.subproject ?? null;
  const rooms = data?.rooms ?? [];
  const extras = subproject?.extras ?? [];
  const totals = data?.totals ?? null;
  const itemPvpIva = (item: RoomItem) => parseDecimal(item.pvp_iva);
  const roomSubtotalIva = (room: Room) =>
    room.items.reduce((total, item) => total + itemPvpIva(item), 0);
  const subprojectPvpIva = rooms.reduce((total, room) => total + roomSubtotalIva(room), 0);
  const plannedCostAmount = rooms.reduce(
    (total, room) =>
      total +
      room.items.reduce(
        (roomTotal, item) => roomTotal + parseDecimal(item.unit_cost) * item.quantity,
        0,
      ),
    0,
  );
  const totalDesvioCosteAmount = rooms.reduce(
    (total, room) =>
      total + room.items.reduce((roomTotal, item) => roomTotal + parseDecimal(item.desvio_coste), 0),
    0,
  );
  const totalMountingAmount = totals?.itemMounting ?? 0;
  const totalExtrasAmount = extras.reduce(
    (total, extra) => total + parseDecimal(extra.amount),
    0,
  );
  const actualCostAmount = totals?.actualCost ?? 0;
  const actualBudgetAmount = totals?.actualBudget ?? 0;
  const plannedBudgetAmount = totals?.plannedBudget ?? 0;
  const aggregatedCost = actualCostAmount + totalMountingAmount + totalExtrasAmount;
  const benefitAmount = actualBudgetAmount - aggregatedCost;
  const plannedVsRealDiff = plannedBudgetAmount - actualBudgetAmount;

  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogAddMode, setCatalogAddMode] = useState<CatalogAddMode>(null);
  const [inlineCatalogDraftRoomId, setInlineCatalogDraftRoomId] = useState<Room['id'] | null>(null);
  const [catalogRoomName, setCatalogRoomName] = useState('');
  const [catalogQuantity, setCatalogQuantity] = useState(1);
  const [catalogSearchField, setCatalogSearchField] = useState<'ref' | 'supplier' | 'description'>('ref');
  const [catalogSearchValue, setCatalogSearchValue] = useState('');
  const [searchRefInput, setSearchRefInput] = useState('');
  const [searchSupplierInput, setSearchSupplierInput] = useState('');
  const [searchDescriptionInput, setSearchDescriptionInput] = useState('');
  const [selectedCatalogItem, setSelectedCatalogItem] = useState<CatalogItem | null>(null);
  const [catalogSupplierId, setCatalogSupplierId] = useState('');
  const [catalogSupplierName, setCatalogSupplierName] = useState('');
  const [catalogSubmitting, setCatalogSubmitting] = useState(false);
  const [catalogCategory, setCatalogCategory] = useState('');
  const [catalogCategoryId, setCatalogCategoryId] = useState('');
  const [catalogCategoryEdited, setCatalogCategoryEdited] = useState(false);

  const [roomToEdit, setRoomToEdit] = useState<Room | null>(null);
  const [roomNameDraft, setRoomNameDraft] = useState('');
  const [renamingRoom, setRenamingRoom] = useState(false);

  const [notesPreview, setNotesPreview] = useState<{ roomId: Room['id']; roomName: string; item: RoomItem } | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [extraDescriptionDraft, setExtraDescriptionDraft] = useState('');
  const [extraAmountDraft, setExtraAmountDraft] = useState('');
  const [editingExtraId, setEditingExtraId] = useState<SubprojectExtra['id'] | null>(null);
  const [editingExtraDescription, setEditingExtraDescription] = useState('');
  const [editingExtraAmount, setEditingExtraAmount] = useState('');
  const [extraToDelete, setExtraToDelete] = useState<SubprojectExtra | null>(null);
  const [savingExtra, setSavingExtra] = useState(false);
  const [deletingExtraId, setDeletingExtraId] = useState<SubprojectExtra['id'] | null>(null);
  const openNotesPreview = (room: Room, item: RoomItem) => {
  setNotesPreview({ roomId: room.id, roomName: room.name, item });
  setNoteDraft(item.notes ?? '');
  };
  const closeNotesPreview = () => {
    setNotesPreview(null);
    setNoteDraft('');
    setSavingNote(false);
  };

  const restoreScroll = useCallback(() => {
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPosRef.current, behavior: 'auto' });
    });
  }, []);

  const openPdfExportDialog = useCallback(() => {
    setSelectedPdfOptionalColumns(DEFAULT_PDF_OPTIONAL_COLUMNS);
    setPdfExportDialogOpen(true);
  }, []);

  const togglePdfOptionalColumn = useCallback((column: SubprojectPdfOptionalColumn, checked: boolean) => {
    setSelectedPdfOptionalColumns((previous) => {
      const nextSelection = new Set(previous);
      if (checked) nextSelection.add(column);
      else nextSelection.delete(column);
      return PDF_OPTIONAL_COLUMN_VALUES.filter((value) => nextSelection.has(value));
    });
  }, []);
  const areAllPdfOptionalColumnsSelected =
    selectedPdfOptionalColumns.length === PDF_OPTIONAL_COLUMN_VALUES.length;
  const toggleAllPdfOptionalColumns = useCallback(() => {
    setSelectedPdfOptionalColumns((previous) =>
      previous.length === PDF_OPTIONAL_COLUMN_VALUES.length ? [] : DEFAULT_PDF_OPTIONAL_COLUMNS,
    );
  }, []);

  const handleExportPdf = useCallback(async (optionalColumns: SubprojectPdfOptionalColumn[]) => {
    if (!subproject) return false;
    setDownloadingPdf(true);
    try {
      const blob = await downloadSubprojectPdf(subproject.id, { optionalColumns });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `subproyecto-${subproject.ref || subproject.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo generar el PDF';
      toast.error(message);
      return false;
    } finally {
      setDownloadingPdf(false);
    }
  }, [downloadSubprojectPdf, subproject]);

  const handleExportExcel = useCallback(async () => {
    if (!subproject) return;
    setDownloadingExcel(true);
    try {
      const blob = await downloadSubprojectExcel(subproject.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `subproyecto-${subproject.ref || subproject.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo generar el Excel';
      toast.error(message);
    } finally {
      setDownloadingExcel(false);
    }
  }, [downloadSubprojectExcel, subproject]);

  const handleConfirmPdfExport = useCallback(async () => {
    const success = await handleExportPdf(selectedPdfOptionalColumns);
    if (success) {
      setPdfExportDialogOpen(false);
    }
  }, [handleExportPdf, selectedPdfOptionalColumns]);

  const roomOptions = useMemo(
    () => rooms.map((room) => ({ label: room.name, value: room.name })),
    [rooms],
  );

  const categoryOptions = useMemo(
    () => categories.map((category) => ({ label: category.name, value: category.id })),
    [categories],
  );

  const duplicateRoomOptions = useMemo(
    () =>
      itemToDuplicate
        ? rooms.map((room) => ({ label: room.name, value: room.id }))
        : [],
    [itemToDuplicate, rooms],
  );

  const duplicateNoRoomOptionsMessage = useMemo(() => {
    if (!itemToDuplicate) return 'No hay estancias disponibles para duplicar este item.';
    if (duplicateRoomOptions.length === 0) {
      return 'No hay estancias disponibles para duplicar este item.';
    }
    return undefined;
  }, [duplicateRoomOptions.length, itemToDuplicate]);

  const duplicateCategoryOptions = useMemo(
    () =>
      itemToDuplicate
        ? categoryOptions.filter((option) => option.value !== itemToDuplicate.item.category_id)
        : [],
    [categoryOptions, itemToDuplicate],
  );

  const supplierOptions = useMemo(
    () =>
      supplierResults.map((supplier) => ({
        label: supplier.name,
        value: supplier.id,
        meta: {
          desvio_comercial_pct: String(supplier.desvio_comercial_pct ?? '0'),
          beneficio_comercial_pct: String(supplier.beneficio_comercial_pct ?? '0'),
        },
      })),
    [supplierResults],
  );

  useEffect(() => {
    setSelectedRoom('all');
    setSelectedState('all');
  }, [subprojectId, rooms.length]);

  useEffect(() => {
    const handleResize = () => {
      setIsViewportAbove1920(window.innerWidth > 1920);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const debouncedCatalogSearch = useDebouncedValue(catalogSearchValue, 250);
  const isCatalogInlineEditing = catalogAddMode === 'inline' && inlineCatalogDraftRoomId !== null;
  const isCatalogAddFlowOpen = catalogAddMode !== null;
  const isCatalogDraftBlockingActions = isCatalogInlineEditing || catalogSubmitting;

  useEffect(() => {
    if (!isCatalogAddFlowOpen) return;
    const query = debouncedCatalogSearch.trim();
    if (!query) {
      setCatalogResults([]);
      return;
    }
    void searchCatalogItems({ query, by: catalogSearchField, limit: 100 });
  }, [isCatalogAddFlowOpen, catalogSearchField, debouncedCatalogSearch, searchCatalogItems, setCatalogResults]);

  useEffect(() => {
    if (!selectedCatalogItem || catalogSupplierId) return;
    const normalizedSupplier = selectedCatalogItem.supplier?.trim().toLowerCase();
    const match = supplierOptions.find(
      (option) => option.label.trim().toLowerCase() === normalizedSupplier,
    );
    if (match) {
      setCatalogSupplierId(match.value);
      setCatalogSupplierName(match.label);
    }
  }, [catalogSupplierId, selectedCatalogItem, supplierOptions]);

  useEffect(() => {
    if (!selectedCatalogItem || catalogCategoryId || catalogCategoryEdited) return;
    if (selectedCatalogItem.category_id) {
      const matchById = categoryOptions.find((option) => option.value === selectedCatalogItem.category_id);
      if (matchById) {
        setCatalogCategoryId(matchById.value);
        setCatalogCategory(matchById.label);
        return;
      }
    }
    const normalizedCategory = selectedCatalogItem.category?.trim().toLowerCase();
    if (!normalizedCategory) return;
    const match = categoryOptions.find(
      (option) => option.label.trim().toLowerCase() === normalizedCategory,
    );
    if (match) {
      setCatalogCategoryId(match.value);
      setCatalogCategory(match.label);
    }
  }, [catalogCategoryEdited, catalogCategoryId, categoryOptions, selectedCatalogItem]);

  useEffect(() => {
  if (refreshVersion === undefined) return;
  scrollPosRef.current = window.scrollY;
  void refetch();
}, [refreshVersion, refetch]);

  const filteredRooms = useMemo(
    () => (selectedRoom === 'all' ? rooms : rooms.filter((room) => room.id === selectedRoom)),
    [rooms, selectedRoom],
  );

  useLayoutEffect(() => {
  if (!isLoading) {
    window.scrollTo({ top: scrollPosRef.current, behavior: 'auto' });
  }
  }, [isLoading]);

  const roomsWithSortedItems = useMemo(
  () =>
    filteredRooms.map((room) => {
      const sortedItems = [...room.items].sort((a, b) =>
        a.ref.localeCompare(b.ref, 'es', { sensitivity: 'base' }),
      );

      const itemsFilteredByState =
        selectedState === 'all'
          ? sortedItems
          : sortedItems.filter((item) => item.state === selectedState);

      return { ...room, items: itemsFilteredByState };
    }),
  [filteredRooms, selectedState],
);

  const roomsWithCategorizedItems = useMemo<RoomWithGroupedItems[]>(
    () =>
      roomsWithSortedItems.map((room) => {
        const grouped = room.items.reduce<Record<string, RoomItem[]>>((acc, item) => {
          const categoryName = item.category?.trim() || DEFAULT_CATEGORY_LABEL;
          acc[categoryName] = acc[categoryName] ?? [];
          acc[categoryName].push(item);
          return acc;
        }, {});

        const groupedItems: ItemsByCategory[] = Object.keys(grouped)
          .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
          .map((category) => ({ category, items: grouped[category] }));

        return { ...room, groupedItems };
      }),
    [roomsWithSortedItems],
  );

  const renderStateBadge = (state: RoomItem['state']) => (
    <Badge variant={stateBadges[state] ?? 'secondary'}>{capitalize(state)}</Badge>
  );

  const getSubprojectStatusBadge = (status: SubprojectDetailType['status']) => (
    <Badge variant={STATUS_BADGE_VARIANTS[status as ProjectStatus] ?? 'secondary'}>
      {formatProjectStatus(status)}
    </Badge>
  );

  const closeItemDialog = useCallback(() => {
    setItemDialogOpen(false);
    setEditingItem(null);
    setEditingRoom(null);
    setActiveRoomId(null);
    setDraftRoomName('');
  }, []);

  const closeCreateCatalogItemDialog = useCallback(() => {
    setCreateCatalogItemDialogOpen(false);
    setDraftRoomName('');
  }, []);

  const closeDuplicateItemDialog = useCallback(() => {
    if (duplicatingItem) return;
    setItemToDuplicate(null);
  }, [duplicatingItem]);

  const normalizeRoomName = (name: string) => name.trim().replace(/\s+/g, ' ');

  const findRoomByName = useCallback(
    (name: string) => {
      const normalized = normalizeRoomName(name).toLowerCase();
      return (
        rooms.find(
          (room) => normalizeRoomName(room.name).toLowerCase() === normalized,
        ) ?? null
      );
    },
    [rooms],
  );

  const ensureRoomId = useCallback(
    async (roomName: string) => {
      const normalized = normalizeRoomName(roomName);
      if (!normalized) {
        throw new Error('Debes indicar un nombre de estancia.');
      }
      const existing = findRoomByName(normalized);
      if (existing) return existing.id;

      const created = await createRoom(subprojectId, { name: normalized });
      return created.id;
    },
    [createRoom, findRoomByName, subprojectId],
  );

  const resetCatalogAddState = useCallback(() => {
    setSelectedCatalogItem(null);
    setSearchRefInput('');
    setSearchSupplierInput('');
    setSearchDescriptionInput('');
    setCatalogSearchValue('');
    setCatalogSupplierId('');
    setCatalogSupplierName('');
    setCatalogCategory('');
    setCatalogCategoryId('');
    setCatalogCategoryEdited(false);
    setCatalogQuantity(1);
    setCatalogSearchField('ref');
    setCatalogResults([]);
  }, [setCatalogResults]);

  const cancelCatalogAddFlow = useCallback(() => {
    setCatalogDialogOpen(false);
    setInlineCatalogDraftRoomId(null);
    setCatalogAddMode(null);
    setCatalogRoomName('');
    resetCatalogAddState();
  }, [resetCatalogAddState]);

  const openCatalogDialogForRoom = useCallback(
    (room?: Room) => {
      if (isCatalogInlineEditing || catalogSubmitting) return;
      resetCatalogAddState();
      const prefilled =
        room?.name ??
        (selectedRoom === 'all'
          ? ''
          : rooms.find((item) => item.id === selectedRoom)?.name ?? '');
      setCatalogRoomName(prefilled);
      setCatalogAddMode('dialog');
      setCatalogDialogOpen(true);
    },
    [catalogSubmitting, isCatalogInlineEditing, resetCatalogAddState, rooms, selectedRoom],
  );

  const openInlineCatalogDraftForRoom = useCallback(
    (room: Room) => {
      if (catalogDialogOpen || isCatalogInlineEditing || catalogSubmitting) return;
      resetCatalogAddState();
      setCatalogRoomName(room.name);
      setInlineCatalogDraftRoomId(room.id);
      setCatalogAddMode('inline');
    },
    [catalogDialogOpen, isCatalogInlineEditing, catalogSubmitting, resetCatalogAddState],
  );

  const openCreateItemDialog = useCallback(() => {
    if (isCatalogInlineEditing) return;
    const prefilled =
      selectedRoom === 'all'
        ? ''
        : rooms.find((room) => room.id === selectedRoom)?.name ?? '';
    setDraftRoomName(prefilled);
    setCreateCatalogItemDialogOpen(true);
  }, [isCatalogInlineEditing, rooms, selectedRoom]);

  const handleEditItem = useCallback((room: Room, item: RoomItem) => {
    setActiveRoomId(room.id);
    setEditingRoom(room);
    setEditingItem(item);
    setDraftRoomName('');
    setItemDialogOpen(true);
  }, []);

  const openDuplicateItemDialog = useCallback((room: Room, item: RoomItem) => {
    if (subproject?.status === 'finalizado' || isCatalogDraftBlockingActions) return;
    setItemToDuplicate({ roomId: room.id, roomName: room.name, item });
  }, [isCatalogDraftBlockingActions, subproject?.status]);

  const handleSelectCatalogItem = useCallback(
    (item: CatalogItem) => {
      setSelectedCatalogItem(item);
      setSearchRefInput(item.ref);
      setSearchSupplierInput(item.supplier);
      setSearchDescriptionInput(item.description);
      setCatalogCategory(item.category ?? '');
      setCatalogCategoryId(item.category_id ?? '');
      setCatalogCategoryEdited(false);
      const normalizedSupplier = item.supplier?.trim().toLowerCase();
      const match = supplierOptions.find(
        (option) => option.label.trim().toLowerCase() === normalizedSupplier,
      );
      setCatalogSupplierId(item.supplier_id ?? match?.value ?? '');
      setCatalogSupplierName(item.supplier ?? match?.label ?? '');
    },
    [supplierOptions],
  );

  const itemFormInitialValues = useMemo<Partial<ItemFormValues> | undefined>(() => {
    if (editingItem) {
      return {
        roomName: editingRoom?.name ?? '',
        ref: editingItem.ref,
        supplier: editingItem.supplier,
        supplierId: editingItem.supplier_id ?? '',
        description: editingItem.description,
        category: editingItem.category ?? '',
        categoryId: editingItem.category_id,
        quantity: editingItem.quantity,
        precio_tarifa: editingItem.precio_tarifa,
        desvio_comercial_pct: editingItem.desvio_comercial_pct,
        beneficio_comercial_pct: editingItem.beneficio_comercial_pct,
        montaje_pct: editingItem.montaje_pct,
        discount: editingItem.discount,
        real_cost: editingItem.real_cost ?? '',
        customRealBudget: Boolean(editingItem.real_cost_modi),
        desvio_personalizado: Boolean(editingItem.desvio_personalizado),
        beneficio_personalizado: Boolean(editingItem.beneficio_personalizado),
        state: editingItem.state,
        notes: editingItem.notes ?? '',
      };
    }
    return undefined;
  }, [editingItem, editingRoom]);

  const createCatalogItemInitialValues = useMemo<Partial<SubprojectCreateCatalogItemForm> | undefined>(
    () => (draftRoomName ? { roomName: draftRoomName } : undefined),
    [draftRoomName],
  );

  const handleCreateCatalogItemSubmit = useCallback(
    async (values: SubprojectCreateCatalogItemForm) => {
      try {
        setItemSubmitting(true);

        const supplierId = values.supplierId.trim();
        const categoryId = values.categoryId.trim();
        const unitCost = parseDecimal(values.unitCost);
        const desvioComercialPct = parseDecimal(values.desvioComercialPct);
        const beneficioComercialPct = parseDecimal(values.beneficioComercialPct);
        const montajePct = parseDecimal(values.montajePct);
        const pvp = unitCost * (1 + beneficioComercialPct / 100) * (1 + montajePct / 100);
        const catalogCost = unitCost * (1 - desvioComercialPct / 100);

        if (!supplierId) throw new Error('Selecciona un proveedor válido.');
        if (!categoryId) throw new Error('Selecciona una categoría válida.');
        if (desvioComercialPct < 0 || desvioComercialPct > 100) {
          throw new Error('El desvío comercial debe estar entre 0 y 100.');
        }
        if (beneficioComercialPct < 0 || beneficioComercialPct > 100) {
          throw new Error('El beneficio comercial debe estar entre 0 y 100.');
        }
        if (montajePct < 0 || montajePct > 100) {
          throw new Error('El montaje debe estar entre 0 y 100.');
        }
        if (pvp < catalogCost) throw new Error('El PVP no puede ser menor que el costo.');

        const roomId = await ensureRoomId(values.roomName);
        const payload: RoomItemCreateFromCatalogPayload = {
          ref: values.ref.trim(),
          supplier: values.supplierName.trim(),
          supplier_id: supplierId,
          description: values.description.trim(),
          category_id: categoryId,
          quantity: Number(values.quantity) || 0,
          unit_cost: unitCost.toFixed(2),
          desvio_comercial_pct: desvioComercialPct.toFixed(2),
          beneficio_comercial_pct: beneficioComercialPct.toFixed(2),
          montaje_pct: montajePct.toFixed(2),
          discount: '0.00',
          desvio_personalizado: Boolean(values.customDesvioComercial),
          beneficio_personalizado: Boolean(values.customBeneficioComercial),
        };

        await createRoomItemFromCatalog(subprojectId, roomId, payload);
        toast.success('Item creado en catálogo y añadido al subproyecto.');
        scrollPosRef.current = window.scrollY;
        await refetch();
        restoreScroll();
        closeCreateCatalogItemDialog();
        onSubprojectUpdated?.();
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : 'No pudimos crear el item. Inténtalo de nuevo.';
        toast.error(message);
      } finally {
        setItemSubmitting(false);
      }
    },
    [
      closeCreateCatalogItemDialog,
      createRoomItemFromCatalog,
      ensureRoomId,
      onSubprojectUpdated,
      refetch,
      restoreScroll,
      subprojectId,
    ],
  );

const handleItemSubmit = useCallback(
  async (values: ItemFormValues) => {
    try {
      setItemSubmitting(true);

      const realCostIsCustom = Boolean(values.customRealBudget);
      const supplierId = values.supplierId || editingItem?.supplier_id;

      if (!supplierId) throw new Error('No se pudo editar el item porque falta el proveedor asociado.');
      if (!values.categoryId) throw new Error('Selecciona una categoría válida para el item.');

      const precioTarifa = parseDecimal(values.precio_tarifa);
      const desvioComercialPct = parseDecimal(values.desvio_comercial_pct);
      const beneficioComercialPct = parseDecimal(values.beneficio_comercial_pct);
      const montajePct = parseDecimal(values.montaje_pct);
      const discountPct = parseDecimal(values.discount);
      if (desvioComercialPct < 0 || desvioComercialPct > 100) {
        throw new Error('El desvío comercial debe estar entre 0 y 100.');
      }
      if (beneficioComercialPct < 0 || beneficioComercialPct > 100) {
        throw new Error('El beneficio comercial debe estar entre 0 y 100.');
      }
      if (montajePct < 0 || montajePct > 100) {
        throw new Error('El montaje debe estar entre 0 y 100.');
      }
      if (discountPct < 0 || discountPct > 100) {
        throw new Error('El descuento debe estar entre 0 y 100.');
      }
      const unitCost = precioTarifa * (1 - desvioComercialPct / 100);
      const pvpUnit = precioTarifa * (1 + beneficioComercialPct / 100) * (1 + montajePct / 100);
      if (pvpUnit < unitCost) {
        throw new Error('El PVP no puede ser menor que el costo.');
      }

      const payload: RoomItemCreatePayload = {
        ref: values.ref.trim(),
        supplier: values.supplier.trim(),
        supplier_id: supplierId ?? undefined,
        description: values.description.trim(),
        category_id: values.categoryId,
        quantity: values.quantity,
        precio_tarifa: precioTarifa.toFixed(2),
        desvio_comercial_pct: desvioComercialPct.toFixed(2),
        beneficio_comercial_pct: beneficioComercialPct.toFixed(2),
        montaje_pct: montajePct.toFixed(2),
        discount: discountPct.toFixed(2),
        desvio_personalizado: Boolean(values.desvio_personalizado),
        beneficio_personalizado: Boolean(values.beneficio_personalizado),
        real_cost: realCostIsCustom ? values.real_cost : null,
        real_cost_modi: realCostIsCustom,
        notes: values.notes?.trim() ? values.notes.trim() : null,
        state: values.state,
      };

      const normalizedNewRoom = normalizeRoomName(values.roomName);
      const normalizedCurrentRoom = editingRoom ? normalizeRoomName(editingRoom.name) : null;

      if (editingItem && editingRoom && normalizedNewRoom && normalizedCurrentRoom && normalizedNewRoom !== normalizedCurrentRoom) {
        const newRoomId = await ensureRoomId(values.roomName);
        const originalPayload: RoomItemCreatePayload = {
          ref: editingItem.ref,
          supplier: editingItem.supplier,
          supplier_id: supplierId,
          description: editingItem.description,
          category_id: editingItem.category_id,
          quantity: editingItem.quantity,
          precio_tarifa: editingItem.precio_tarifa,
          desvio_comercial_pct: editingItem.desvio_comercial_pct,
          beneficio_comercial_pct: editingItem.beneficio_comercial_pct,
          montaje_pct: editingItem.montaje_pct,
          discount: editingItem.discount,
          desvio_personalizado: editingItem.desvio_personalizado,
          beneficio_personalizado: editingItem.beneficio_personalizado,
          real_cost: editingItem.real_cost,
          real_cost_modi: editingItem.real_cost_modi,
          notes: editingItem.notes,
          state: editingItem.state,
        };

        await deleteRoomItem(subprojectId, editingRoom.id, editingItem.id);
        try {
          await createRoomItem(subprojectId, newRoomId, payload);
          toast.success('Item movido a la nueva estancia.');
        } catch (moveError) {
          await createRoomItem(subprojectId, editingRoom.id, originalPayload);
          throw moveError;
        }
      } else if (editingItem && editingRoom) {
        await updateRoomItem(subprojectId, editingRoom.id, editingItem.id, payload);
        toast.success('Item actualizado correctamente.');
      } else {
        throw new Error('No hay item seleccionado para editar.');
      }

      scrollPosRef.current = window.scrollY;
      await refetch();
      restoreScroll();
      closeItemDialog();
      onSubprojectUpdated?.();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'No pudimos guardar el item. Inténtalo de nuevo.';
      toast.error(message);
    } finally {
      setItemSubmitting(false);
    }
  },
  [editingItem, editingRoom, ensureRoomId, updateRoomItem, deleteRoomItem, subprojectId, createRoomItem, refetch, closeItemDialog],
);

  const canAddItem =
    subproject?.status !== 'finalizado' &&
    !itemSubmitting &&
    !catalogSubmitting &&
    !isCatalogInlineEditing;

  const handleDeleteItem = useCallback(
    async () => {
      if (!itemToDelete || subproject?.status === 'finalizado' || deletingItemId) {
        return;
      }
      const { roomId, item } = itemToDelete;
      setDeletingItemId(item.id);
      try {
        await deleteRoomItem(subprojectId, roomId, item.id);
        toast.success('Item eliminado correctamente.');
        scrollPosRef.current = window.scrollY;
        await refetch();
        restoreScroll();
        setItemToDelete(null);
        onSubprojectUpdated?.();
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : 'No pudimos eliminar el item. Inténtalo de nuevo.';
        toast.error(message);
      } finally {
        setDeletingItemId(null);
      }
    },
    [itemToDelete, subproject?.status, deletingItemId, deleteRoomItem, subprojectId, refetch],
  );

  const handleDuplicateItemSubmit = useCallback(
    async (values: SubprojectDuplicateItemForm) => {
      if (!itemToDuplicate) return;
      if (subproject?.status === 'finalizado') {
        toast.error('No puedes duplicar items en un subproyecto finalizado.');
        return;
      }
      if (values.categoryId === itemToDuplicate.item.category_id) {
        toast.error('Selecciona una categoría distinta de la actual.');
        return;
      }
      if (!Number.isInteger(values.quantity) || values.quantity <= 0) {
        toast.error('La cantidad debe ser un entero mayor que 0.');
        return;
      }
      const targetRoom = rooms.find((room) => room.id === values.targetRoomId);
      if (!targetRoom) {
        toast.error('Selecciona una estancia destino válida.');
        return;
      }
      if (
        targetRoom.items.some(
          (candidate) =>
            normalizeItemRef(candidate.ref) === normalizeItemRef(itemToDuplicate.item.ref)
            && candidate.category_id === values.categoryId,
        )
      ) {
        toast.error(
          `Conflicto local: ya existe un item con esa referencia en ${targetRoom.name} para la categoría seleccionada.`,
        );
        return;
      }

      setDuplicatingItem(true);
      try {
        await duplicateRoomItem(
          subprojectId,
          itemToDuplicate.roomId,
          itemToDuplicate.item.id,
          {
            target_room_id: values.targetRoomId,
            category_id: values.categoryId,
            quantity: values.quantity,
          },
        );
        toast.success('Item duplicado correctamente.');
        scrollPosRef.current = window.scrollY;
        await refetch();
        restoreScroll();
        setItemToDuplicate(null);
        onSubprojectUpdated?.();
      } catch (requestError) {
        if (isAxiosError(requestError)) {
          const detail =
            typeof requestError.response?.data?.detail === 'string'
              ? requestError.response.data.detail
              : null;
          if (requestError.response?.status === 409 && detail) {
            toast.error(detail);
            return;
          }
          if (detail) {
            toast.error(detail);
            return;
          }
        }
        const message =
          requestError instanceof Error
            ? requestError.message
            : 'No pudimos duplicar el item. Inténtalo de nuevo.';
        toast.error(message);
      } finally {
        setDuplicatingItem(false);
      }
    },
    [duplicateRoomItem, itemToDuplicate, onSubprojectUpdated, refetch, restoreScroll, rooms, subproject?.status, subprojectId],
  );

  const handleSaveNote = async () => {
  if (!notesPreview || savingNote) return;
  setSavingNote(true);

  try {
    const item = notesPreview.item;
    const supplierId = item.supplier_id;

    if (!supplierId) {
      throw new Error('No se pudo guardar la nota porque falta el proveedor del item.');
    }

    const payload: RoomItemUpdatePayload = {
      ref: item.ref,
      supplier: item.supplier,
      supplier_id: supplierId,
      description: item.description,
      category_id: item.category_id,
      quantity: item.quantity,
      precio_tarifa: item.precio_tarifa,
      desvio_comercial_pct: item.desvio_comercial_pct,
      beneficio_comercial_pct: item.beneficio_comercial_pct,
      montaje_pct: item.montaje_pct,
      discount: item.discount,
      desvio_personalizado: item.desvio_personalizado,
      beneficio_personalizado: item.beneficio_personalizado,
      real_cost: item.real_cost,
      real_cost_modi: item.real_cost_modi,
      notes: noteDraft.trim() || null,
      state: item.state,
    };

    await updateRoomItem(subprojectId, notesPreview.roomId, item.id, payload);
    toast.success('Notas guardadas correctamente.');
    scrollPosRef.current = window.scrollY;
    await refetch();
    restoreScroll();
    closeNotesPreview();
    onSubprojectUpdated?.();
  } catch (error) {
    toast.error('No se pudo guardar la nota. Inténtalo de nuevo.');
  } finally {
    setSavingNote(false);
  }
};

  const submitCatalogSelectionToRoom = useCallback(
    async (roomNameOverride?: string) => {
      if (!selectedCatalogItem) {
        toast.error('Selecciona un item del catálogo.');
        return;
      }
      const normalizedRoom = normalizeRoomName(roomNameOverride ?? catalogRoomName);
      if (!normalizedRoom) {
        toast.error('Indica la estancia donde se añadirá.');
        return;
      }
      if (catalogQuantity <= 0) {
        toast.error('La cantidad debe ser mayor que 0.');
        return;
      }
      if (!catalogSupplierId) {
        toast.error('Selecciona un proveedor válido.');
        return;
      }
      if (!catalogCategoryId) {
        toast.error('Selecciona una categoría válida.');
        return;
      }

      setCatalogSubmitting(true);
      try {
        const roomId = await ensureRoomId(normalizedRoom);
        const supplierMeta = supplierOptions.find((option) => option.value === catalogSupplierId)?.meta;
        const desvioComercialPct = Number(supplierMeta?.desvio_comercial_pct ?? selectedCatalogItem.desvio_comercial_pct ?? 0);
        const beneficioComercialPct = Number(supplierMeta?.beneficio_comercial_pct ?? selectedCatalogItem.beneficio_comercial_pct ?? 0);
        const payload: RoomItemCreatePayload = {
          ref: selectedCatalogItem.ref,
          supplier: catalogSupplierName || selectedCatalogItem.supplier,
          supplier_id: catalogSupplierId,
          description: selectedCatalogItem.description,
          category_id: catalogCategoryId,
          quantity: catalogQuantity,
          precio_tarifa: selectedCatalogItem.unit_cost,
          desvio_comercial_pct: desvioComercialPct.toFixed(2),
          beneficio_comercial_pct: beneficioComercialPct.toFixed(2),
          montaje_pct: '0.00',
          discount: '0.00',
          desvio_personalizado: false,
          beneficio_personalizado: false,
          real_cost: null,
          real_cost_modi: false,
          notes: null,
          state: 'pendiente',
        };

        await createRoomItem(subprojectId, roomId, payload);
        toast.success('Item añadido desde catálogo.');
        scrollPosRef.current = window.scrollY;
        await refetch();
        restoreScroll();
        cancelCatalogAddFlow();
        onSubprojectUpdated?.();
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : 'No pudimos añadir el item desde catálogo. Inténtalo de nuevo.';
        toast.error(message);
      } finally {
        setCatalogSubmitting(false);
      }
    },
    [
      catalogQuantity,
      catalogRoomName,
      catalogCategoryId,
      catalogSupplierId,
      catalogSupplierName,
      cancelCatalogAddFlow,
      createRoomItem,
      ensureRoomId,
      onSubprojectUpdated,
      refetch,
      restoreScroll,
      selectedCatalogItem,
      supplierOptions,
      subprojectId,
    ],
  );

  const handleCatalogSubmit = useCallback(async () => {
    await submitCatalogSelectionToRoom();
  }, [submitCatalogSelectionToRoom]);

  const handleInlineCatalogSubmit = useCallback(
    async (room: Room) => {
      await submitCatalogSelectionToRoom(room.name);
    },
    [submitCatalogSelectionToRoom],
  );

  const resetExtraEditor = useCallback(() => {
    setEditingExtraId(null);
    setEditingExtraDescription('');
    setEditingExtraAmount('');
  }, []);

  const handleStartEditExtra = useCallback((extra: SubprojectExtra) => {
    setEditingExtraId(extra.id);
    setEditingExtraDescription(extra.description);
    setEditingExtraAmount(parseDecimal(extra.amount).toFixed(2));
  }, []);

  const handleCreateExtra = useCallback(async () => {
    if (savingExtra) return;

    const description = extraDescriptionDraft.trim();
    const amountValue = parseRequiredAmountInput(extraAmountDraft);
    if (!description) {
      toast.error('La descripción del extra es obligatoria.');
      return;
    }
    if (amountValue === null || amountValue < 0) {
      toast.error('El gasto debe ser un número mayor o igual a 0.');
      return;
    }

    const payload: SubprojectExtraCreatePayload = {
      description,
      amount: amountValue.toFixed(2),
    };

    setSavingExtra(true);
    try {
      await createSubprojectExtra(subprojectId, payload);
      toast.success('Extra añadido correctamente.');
      setExtraDescriptionDraft('');
      setExtraAmountDraft('');
      scrollPosRef.current = window.scrollY;
      await refetch();
      restoreScroll();
      onSubprojectUpdated?.();
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : 'No pudimos añadir el extra. Inténtalo de nuevo.';
      toast.error(message);
    } finally {
      setSavingExtra(false);
    }
  }, [
    createSubprojectExtra,
    extraAmountDraft,
    extraDescriptionDraft,
    onSubprojectUpdated,
    refetch,
    restoreScroll,
    savingExtra,
    subprojectId,
  ]);

  const handleSaveExtra = useCallback(
    async (extraId: SubprojectExtra['id']) => {
      if (savingExtra) return;

      const description = editingExtraDescription.trim();
      const amountValue = parseRequiredAmountInput(editingExtraAmount);
      if (!description) {
        toast.error('La descripción del extra es obligatoria.');
        return;
      }
      if (amountValue === null || amountValue < 0) {
        toast.error('El gasto debe ser un número mayor o igual a 0.');
        return;
      }

      const payload: SubprojectExtraUpdatePayload = {
        description,
        amount: amountValue.toFixed(2),
      };

      setSavingExtra(true);
      try {
        await updateSubprojectExtra(subprojectId, extraId, payload);
        toast.success('Extra actualizado correctamente.');
        scrollPosRef.current = window.scrollY;
        await refetch();
        restoreScroll();
        resetExtraEditor();
        onSubprojectUpdated?.();
      } catch (requestError) {
        const message =
          requestError instanceof Error
            ? requestError.message
            : 'No pudimos actualizar el extra. Inténtalo de nuevo.';
        toast.error(message);
      } finally {
        setSavingExtra(false);
      }
    },
    [
      editingExtraAmount,
      editingExtraDescription,
      onSubprojectUpdated,
      refetch,
      resetExtraEditor,
      restoreScroll,
      savingExtra,
      subprojectId,
      updateSubprojectExtra,
    ],
  );

  const handleDeleteExtra = useCallback(async () => {
    if (!extraToDelete || savingExtra || deletingExtraId) return;

    setDeletingExtraId(extraToDelete.id);
    try {
      await deleteSubprojectExtra(subprojectId, extraToDelete.id);
      toast.success('Extra eliminado correctamente.');
      scrollPosRef.current = window.scrollY;
      await refetch();
      restoreScroll();
      setExtraToDelete(null);
      if (editingExtraId === extraToDelete.id) {
        resetExtraEditor();
      }
      onSubprojectUpdated?.();
    } catch (requestError) {
      const message =
        requestError instanceof Error
          ? requestError.message
          : 'No pudimos eliminar el extra. Inténtalo de nuevo.';
      toast.error(message);
    } finally {
      setDeletingExtraId(null);
    }
  }, [
    deleteSubprojectExtra,
    deletingExtraId,
    editingExtraId,
    extraToDelete,
    onSubprojectUpdated,
    refetch,
    resetExtraEditor,
    restoreScroll,
    savingExtra,
    subprojectId,
  ]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack} className="p-2" disabled={isCatalogDraftBlockingActions}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl text-gray-900">
              {subproject?.name ?? 'Subproyecto'}
            </h1>
            <p className="text-gray-600">
              {subproject ? `${subproject.type}${subproject.ref ? ` • Ref: ${subproject.ref}` : ''}` : 'Selecciona un subproyecto para ver los detalles'}
            </p>
            {isLoading && (
              <div className="mt-2 flex items-center text-sm text-blue-600">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Cargando información del subproyecto…
              </div>
            )}
            {error && (
              <div className="mt-2 flex items-center text-sm text-red-600">
                <AlertCircle className="mr-2 h-4 w-4" />
                No se pudo cargar el subproyecto. Intenta de nuevo más tarde.
              </div>
            )}
          </div>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => void handleExportExcel()}
            disabled={!subproject || downloadingExcel || isCatalogDraftBlockingActions}
          >
            {downloadingExcel ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Exportar Excel
          </Button>

          <Button
            variant="outline"
            onClick={openPdfExportDialog}
            disabled={!subproject || downloadingPdf || isCatalogDraftBlockingActions}
          >
            {downloadingPdf ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exportar PDF
          </Button>
                          <Button
  className="bg-blue-600 hover:bg-blue-700"
  disabled={isCatalogDraftBlockingActions}
  onClick={() => setOrderDialogOpen(true)}
>
  <Plus className="mr-2 h-4 w-4" />
  Generar pedidos
</Button>
        </div>
      </div>

      {/* Subproject Info */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          {subproject ? (
            <div className="grid grid-cols-1 gap-6 text-center md:grid-cols-2 lg:grid-cols-6">
              <div>
                <p className="mb-1 text-sm text-gray-600">Tipo</p>
                <p className="text-gray-900">{subproject.type}</p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-600">Referencia</p>
                <p className="text-gray-900">{subproject.ref ?? '—'}</p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-600">Estado</p>
                <div className="flex justify-center">{getSubprojectStatusBadge(subproject.status)}</div>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-600">Presupuesto planificado</p>
                <p className="text-xl text-gray-900">{formatCurrency(parseDecimal(subproject.planned_budget))}</p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-600">PVP Total</p>
                <p className="text-xl text-gray-900">
                  {formatCurrency(parseDecimal(subproject?.actual_budget))}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  PVP IVA: {formatCurrency(subprojectPvpIva)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-600">Margen Real</p>
                <p className="text-xl text-green-600">{formatPercent(parseDecimal(subproject.real_margin))}</p>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              Selecciona un subproyecto para ver sus cifras.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Room Filter */}
      <Card className="shadow-sm">
  <CardContent className="py-4">
    <div className="flex flex-wrap items-center gap-4 justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Home className="h-5 w-5 text-gray-600" />
        </div>
        <Select
          value={selectedRoom}
          onValueChange={setSelectedRoom}
          disabled={rooms.length === 0 || isCatalogDraftBlockingActions}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="Selecciona una estancia" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las estancias</SelectItem>
            {rooms.map((room) => (
              <SelectItem key={room.id} value={room.id}>
                {room.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={selectedState}
          onValueChange={(value) => setSelectedState(value as ItemStateFilter)}
          disabled={rooms.length === 0 || isCatalogDraftBlockingActions}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Selecciona un estado" />
          </SelectTrigger>
          <SelectContent>
            {ITEM_STATE_FILTERS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        size="sm"
        className="bg-blue-600 hover:bg-blue-700"
        disabled={!canAddItem}
        onClick={openCreateItemDialog}
      >
        <Plus className="mr-2 h-4 w-4" />
        Crear item
      </Button>
    </div>
  </CardContent>
</Card>


      {/* Items by Room */}
      <div className="space-y-6">
        {isLoading ? (
          <Card className="shadow-sm">
            <CardContent className="flex items-center justify-center py-12 text-gray-600">
              <Loader2 className="mr-2 h-5 w-5 animate-spin text-blue-600" />
              Cargando estancias…
            </CardContent>
          </Card>
        ) : roomsWithCategorizedItems.length > 0 ? (
          roomsWithCategorizedItems.map((room) => (
            <Card key={room.id} className="shadow-sm w-full max-w-full">
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <div className="flex items-center space-x-3">
                  <CardTitle className="text-lg">{room.name}</CardTitle>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                      {room.items.length} items
                    </Badge>
                    <Button
  size="sm"
  variant="outline"
  className="h-7 rounded-full border-blue-100 bg-blue-50 px-3 py-0 text-xs font-semibold text-blue-700"
  disabled={subproject?.status === 'finalizado' || isCatalogDraftBlockingActions}
  onClick={() => {
    setRoomToEdit(room);
    setRoomNameDraft(room.name);
  }}
>
  <Pencil className="mr-1 h-3.5 w-3.5" />
  Editar estancia
</Button>
                </div>
                <div className="flex flex-col items-center text-center md:items-end md:text-right">
                  <div className="flex flex-col items-center text-center md:flex-row md:items-start md:gap-10">
                    <div className="flex flex-col items-center text-center">
                      <p className="text-sm text-gray-600">Subtotal Coste + Montaje</p>
                      <p className="text-xl text-gray-900">
                        {formatCurrency(roomCost(room))}
                      </p>

                    </div>
                    <div className="flex flex-col items-center text-center">
                      <p className="text-sm text-gray-600">Subtotal PVP</p>
                      <p className="text-xl text-gray-900">{formatCurrency(roomSubtotal(room))}</p>
                      <p className="mt-1 text-sm text-gray-500">
                        PVP IVA: {formatCurrency(roomSubtotalIva(room))}
                      </p>
                      <p className="mt-2 text-sm text-green-600 md:self-end">Margen: {roomMargin(room)}%</p>
                    </div>
                    <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                        disabled={!canAddItem}
                        onClick={() => openCatalogDialogForRoom(room)}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Añadir item
                      </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="max-w-full">
                <Table className="table-fixed text-xs">
                  <TableHeader>
                    <TableRow style={ROOM_ITEM_SEPARATOR_ROW_STYLE}>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.ref }}
                        title="Referencia"
                        className={COMPACT_HEAD_CLASS}
                      >
                        Referencia
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.supplier }}
                        title="Proveedor"
                        className={COMPACT_HEAD_CLASS}
                      >
                        Proveedor
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.description }}
                        title="Descripción"
                        className={COMPACT_HEAD_CLASS}
                      >
                        Descripción
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.quantity }}
                        title="Unidades"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        UD
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.pvpUnit }}
                        title="PVP unitario"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        PVP/u
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.discount }}
                        title="Descuento sobre PVP"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Dto %
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.pvpTotal }}
                        title="PVP total"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        PVP T.
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.pvpIva }}
                        title="PVP con IVA"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        PVP+IVA
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.precioTarifa }}
                        title="Precio tarifa"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Tarifa
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.desvioComercialPct }}
                        title="Desvío comercial (%)"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Desv.%
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.beneficioComercialPct }}
                        title="Beneficio comercial (%)"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Ben.%
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.montajePct }}
                        title="Montaje (%)"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Mon.%
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.unitCost }}
                        title="Coste unitario"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Coste/u
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.costTotal }}
                        title="Coste total"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        {isViewportAbove1920 ? 'Coste total' : 'Coste T.'}
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.mounting }}
                        title="Montaje (€)"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Mon.€
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.marginTotal }}
                        title="Margen total"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Margen €
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.realCost }}
                        title="Coste real"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        {isViewportAbove1920 ? 'Coste real' : 'Coste R.'}
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.desvioCoste }}
                        title="Desvío de coste"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Desv. coste
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.state }}
                        title="Estado"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Estado
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.notes }}
                        title="Notas"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        Nota
                      </TableHead>
                      <TableHead
                        style={{ width: ROOM_ITEM_COLUMN_WIDTHS.actions }}
                        title="Acciones"
                        className={COMPACT_HEAD_CENTER_CLASS}
                      >
                        <div className="grid grid-cols-[1fr_20px] items-center">
                          <span className="justify-self-center">Acciones</span>
                          <span className="block h-5 w-5" aria-hidden />
                        </div>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody className="[&_td]:py-1.5">
                    {room.items.length > 0 ? (
                      room.groupedItems.map((group) => (
                        <Fragment key={`category-${room.id}-${group.category}`}>
                          <TableRow className="bg-gray-50" style={ROOM_ITEM_SEPARATOR_ROW_STYLE}>
                            <TableCell
                              colSpan={TABLE_TOTAL_COLUMNS}
                              className="!whitespace-normal border-b-0 py-3 text-sm font-semibold tracking-wide text-gray-700"
                            >
                              {group.category}
                            </TableCell>
                          </TableRow>
                          {group.items.map((item) => {
                            const precioTarifa = parseDecimal(item.precio_tarifa);
                            const desvioComercialPct = parseDecimal(item.desvio_comercial_pct);
                            const beneficioComercialPct = parseDecimal(item.beneficio_comercial_pct);
                            const montajePct = parseDecimal(item.montaje_pct);
                            const unitCost = parseDecimal(item.unit_cost);
                            const discount = parseDecimal(item.discount);
                            const pvp = parseDecimal(item.pvp);
                            const mounting = parseDecimal(item.mounting);
                            const costTotal = unitCost * item.quantity;
                            const realCost = itemRealCostTotal(item);
                            const pvpNet = itemNetUnitPvp(item);
                            const pvpTotal = pvpNet * item.quantity;
                            const pvpIva = itemPvpIva(item);
                            const marginAmount = pvpTotal - costTotal - mounting;
                            const desvioCoste = parseDecimal(item.desvio_coste);
                            const highlightDesvioComercial = Boolean(item.desvio_personalizado);
                            const highlightBeneficioComercial = Boolean(item.beneficio_personalizado);
                            const highlightRealCost = Boolean(item.real_cost_modi);
                            const desvioCosteColorClass =
                              desvioCoste > 0
                                ? 'text-green-600'
                                : desvioCoste < 0
                                  ? 'text-red-600'
                                  : 'text-gray-900';
                            const hasNotes = Boolean(item.notes?.trim());
                            return (
                              <TableRow key={item.id} style={ROOM_ITEM_SEPARATOR_ROW_STYLE}>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.ref }}
                                  className="text-blue-600 !whitespace-normal break-words"
                                >
                                  {item.ref}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.supplier }}
                                  className="text-gray-600 !whitespace-normal break-words"
                                >
                                  {item.supplier}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.description }}
                                  className="!whitespace-normal break-words"
                                >
                                  <div className="flex flex-col">
                                    <span>{item.description}</span>
                                    {/* <span className="text-xs text-gray-500">
                                      Categoría: {item.category ?? '—'}
                                    </span> */}
                                  </div>
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.quantity }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {item.quantity}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.pvpUnit }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(pvp)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.discount }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {discount > 0 ? (
                                    <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                                      {formatPercent(discount)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-700">{formatPercent(discount)}</span>
                                  )}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.pvpTotal }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(pvpTotal)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.pvpIva }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(pvpIva)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.precioTarifa }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(precioTarifa)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.desvioComercialPct }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {renderHighlightedValue(
                                    formatPercent(desvioComercialPct),
                                    highlightDesvioComercial,
                                  )}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.beneficioComercialPct }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {renderHighlightedValue(
                                    formatPercent(beneficioComercialPct),
                                    highlightBeneficioComercial,
                                  )}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.montajePct }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatPercent(montajePct)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.unitCost }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(unitCost)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.costTotal }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(costTotal)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.mounting }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(mounting)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.marginTotal }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {formatCurrency(marginAmount)}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.realCost }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  {renderHighlightedValue(
                                    realCost > 0 ? formatCurrency(realCost) : '—',
                                    highlightRealCost && realCost > 0,
                                  )}
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.desvioCoste }}
                                  className="whitespace-nowrap px-1 text-center"
                                >
                                  <span className={desvioCosteColorClass}>{formatCurrency(desvioCoste)}</span>
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.state }}
                                  className="px-1 text-center"
                                >
                                  <div className="flex justify-center">
                                    {renderStateBadge(item.state)}
                                  </div>
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.notes }}
                                  className="px-1 text-center"
                                >
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`inline-flex h-7 items-center gap-1 border px-1.5 py-1 ${
                                      hasNotes ? 'border-orange-300 bg-orange-50 text-orange-600' : 'border-gray-200 bg-gray-50 text-gray-500'
                                    }`}
                                    disabled={isCatalogDraftBlockingActions}
                                    onClick={() => openNotesPreview(room, item)}
                                  >
                                    <StickyNote className="h-4 w-4" /> {/* Icono alusivo a notas */}
                                  </Button>
                                </TableCell>
                                <TableCell
                                  style={{ width: ROOM_ITEM_COLUMN_WIDTHS.actions }}
                                  className="px-1 text-center"
                                >
                                  <div className="grid grid-cols-[1fr_20px] items-center">
                                    <div className="flex flex-col items-center gap-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-1.5 text-xs leading-none"
                                        disabled={itemSubmitting || duplicatingItem || subproject?.status === 'finalizado' || isCatalogDraftBlockingActions}
                                        onClick={() => handleEditItem(room, item)}
                                      >
                                        Editar
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 px-1.5 text-xs leading-none text-red-600"
                                        disabled={duplicatingItem || subproject?.status === 'finalizado' || isCatalogDraftBlockingActions}
                                        onClick={() => setItemToDelete({ roomId: room.id, item })}
                                      >
                                        Eliminar
                                      </Button>
                                    </div>
                                    {subproject?.status !== 'finalizado' && !isCatalogDraftBlockingActions ? (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        title="Duplicar"
                                        aria-label={`Duplicar ${item.ref}`}
                                        className="h-5 w-5 justify-self-end rounded-full border border-gray-200 bg-gray-100 p-0 text-gray-600 hover:bg-gray-200 hover:text-gray-700"
                                        disabled={itemSubmitting || duplicatingItem}
                                        onClick={() => openDuplicateItemDialog(room, item)}
                                      >
                                        <Copy className="size-2" />
                                      </Button>
                                    ) : (
                                      <span className="block h-5 w-5 justify-self-end" aria-hidden />
                                    )}
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </Fragment>
                      ))
                    ) : (
                      <TableRow style={ROOM_ITEM_SEPARATOR_ROW_STYLE}>
                        <TableCell colSpan={TABLE_TOTAL_COLUMNS} className="py-6 text-center text-gray-500">
                          Esta estancia no tiene items todavía.
                        </TableCell>
                      </TableRow>
                    )}
                    {isCatalogInlineEditing && inlineCatalogDraftRoomId === room.id ? (
                      <TableRow className="bg-blue-50/30">
                        <TableCell colSpan={TABLE_TOTAL_COLUMNS} className="py-4">
                          <div className="space-y-4 rounded-md border border-blue-100 bg-blue-50 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-medium text-blue-900">
                                Añadiendo a esta estancia: <span className="font-semibold">{room.name}</span>
                              </p>
                              <p className="text-xs text-blue-700">
                                Completa la búsqueda y añade o cancela para continuar.
                              </p>
                            </div>

                            <div className="overflow-visible pb-12">
                              <div className="grid min-w-[980px] grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_220px_110px] items-start gap-3">
                                <CatalogSearchInput
                                  label="Referencia"
                                  placeholder="Ej. REF-001"
                                  value={searchRefInput}
                                  field="ref"
                                  items={catalogItems}
                                  loading={catalogLoading}
                                  disabled={catalogSubmitting}
                                  renderInPortal
                                  onFocus={() => {
                                    setCatalogSearchField('ref');
                                    setCatalogSearchValue(searchRefInput);
                                  }}
                                  onChange={(next) => {
                                    setCatalogSearchField('ref');
                                    setSearchRefInput(next);
                                    setCatalogSearchValue(next);
                                    setSelectedCatalogItem(null);
                                  }}
                                  onSelect={handleSelectCatalogItem}
                                />
                                <CatalogSearchInput
                                  label="Proveedor"
                                  placeholder="Marca o distribuidor"
                                  value={searchSupplierInput}
                                  field="supplier"
                                  items={catalogItems}
                                  loading={catalogLoading}
                                  disabled={catalogSubmitting}
                                  renderInPortal
                                  onFocus={() => {
                                    setCatalogSearchField('supplier');
                                    setCatalogSearchValue(searchSupplierInput);
                                  }}
                                  onChange={(next) => {
                                    setCatalogSearchField('supplier');
                                    setSearchSupplierInput(next);
                                    setCatalogSearchValue(next);
                                    setSelectedCatalogItem(null);
                                  }}
                                  onSelect={handleSelectCatalogItem}
                                />
                                <CatalogSearchInput
                                  label="Descripción"
                                  placeholder="Ej. Sofá 3 plazas"
                                  value={searchDescriptionInput}
                                  field="description"
                                  items={catalogItems}
                                  loading={catalogLoading}
                                  disabled={catalogSubmitting}
                                  renderInPortal
                                  onFocus={() => {
                                    setCatalogSearchField('description');
                                    setCatalogSearchValue(searchDescriptionInput);
                                  }}
                                  onChange={(next) => {
                                    setCatalogSearchField('description');
                                    setSearchDescriptionInput(next);
                                    setCatalogSearchValue(next);
                                    setSelectedCatalogItem(null);
                                  }}
                                  onSelect={handleSelectCatalogItem}
                                />
                                <div className="space-y-1.5">
                                  <p className="text-sm font-medium text-gray-700">Categoría</p>
                                  <CategorySearchInput
                                    value={catalogCategory}
                                    options={categoryOptions}
                                    loading={categoriesLoading}
                                    disabled={catalogSubmitting}
                                    renderInPortal
                                    onChange={(next) => {
                                      setCatalogCategoryEdited(true);
                                      setCatalogCategory(next);
                                      setCatalogCategoryId('');
                                    }}
                                    onSelect={(option) => {
                                      setCatalogCategoryEdited(false);
                                      setCatalogCategory(option.label);
                                      setCatalogCategoryId(option.value);
                                    }}
                                  />
                                </div>
                                <div className="space-y-1.5">
                                  <p className="text-sm font-medium text-gray-700">Cantidad</p>
                                  <Input
                                    type="number"
                                    min={1}
                                    step={1}
                                    value={catalogQuantity}
                                    onChange={(event) => setCatalogQuantity(Number(event.target.value) || 0)}
                                    disabled={catalogSubmitting}
                                    inputMode="numeric"
                                  />
                                </div>
                              </div>
                            </div>

                            {selectedCatalogItem ? (
                              <div className="rounded-md bg-white p-3 text-sm text-gray-800">
                                <p className="font-semibold text-gray-900">
                                  {selectedCatalogItem.ref} • {selectedCatalogItem.supplier}
                                </p>
                                <p className="text-gray-700">{selectedCatalogItem.description}</p>
                                <p className="text-xs text-gray-600">
                                  Categoría: {selectedCatalogItem.category ?? 'Sin categoría'} • Precio tarifa: {formatCurrency(parseDecimal(selectedCatalogItem.unit_cost))} • Coste: {formatCurrency(parseDecimal(selectedCatalogItem.cost_real))} • PVP: {formatCurrency(parseDecimal(selectedCatalogItem.pvp))} • Desvío comercial: {formatPercent(parseDecimal(selectedCatalogItem.desvio_comercial_pct))} • Beneficio comercial: {formatPercent(parseDecimal(selectedCatalogItem.beneficio_comercial_pct))}
                                </p>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-600">
                                Selecciona un item del catálogo para precargar sus valores.
                              </p>
                            )}

                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={cancelCatalogAddFlow} disabled={catalogSubmitting}>
                                Cancelar
                              </Button>
                              <Button
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => void handleInlineCatalogSubmit(room)}
                                disabled={catalogSubmitting || !selectedCatalogItem || !catalogCategoryId}
                              >
                                {catalogSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Añadir item
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={TABLE_TOTAL_COLUMNS} className="py-4">
                          <div className="flex justify-center">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="border-dashed"
                              disabled={!canAddItem || catalogDialogOpen}
                              onClick={() => openInlineCatalogDraftForRoom(room)}
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Añadir item
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))
        ) : (
          <Card className="shadow-sm">
            <CardContent className="py-12 text-center text-gray-500">
              No se encontraron estancias para este subproyecto.
            </CardContent>
          </Card>
        )}
      </div>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle>Extras</CardTitle>
          <p className="text-sm text-gray-600">
            Añade gastos adicionales del subproyecto (transporte, imprevistos, etc.).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Gasto</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {extras.length > 0 ? (
                  extras.map((extra) => {
                    const isEditing = editingExtraId === extra.id;
                    return (
                      <TableRow key={extra.id}>
                        <TableCell>
                          {isEditing ? (
                            <Input
                              value={editingExtraDescription}
                              onChange={(event) => setEditingExtraDescription(event.target.value)}
                              disabled={savingExtra || Boolean(deletingExtraId)}
                              placeholder="Descripción del extra"
                            />
                          ) : (
                            <span className="text-gray-900">{extra.description}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <Input
                              value={editingExtraAmount}
                              onChange={(event) => setEditingExtraAmount(event.target.value)}
                              disabled={savingExtra || Boolean(deletingExtraId)}
                              placeholder="0,00"
                              inputMode="decimal"
                              className="ml-auto max-w-[120px] text-right"
                            />
                          ) : (
                            formatCurrency(parseDecimal(extra.amount))
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {isEditing ? (
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={resetExtraEditor}
                                disabled={savingExtra || Boolean(deletingExtraId)}
                              >
                                Cancelar
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => void handleSaveExtra(extra.id)}
                                disabled={savingExtra || Boolean(deletingExtraId)}
                              >
                                {savingExtra ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                                Guardar
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleStartEditExtra(extra)}
                                disabled={savingExtra || Boolean(deletingExtraId)}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => setExtraToDelete(extra)}
                                disabled={savingExtra || Boolean(deletingExtraId)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Eliminar
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} className="py-6 text-center text-gray-500">
                      Aún no hay extras registrados.
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>
                    <Input
                      value={extraDescriptionDraft}
                      onChange={(event) => setExtraDescriptionDraft(event.target.value)}
                      disabled={savingExtra || Boolean(deletingExtraId)}
                      placeholder="Descripción del nuevo extra"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Input
                      value={extraAmountDraft}
                      onChange={(event) => setExtraAmountDraft(event.target.value)}
                      disabled={savingExtra || Boolean(deletingExtraId)}
                      placeholder="0,00"
                      inputMode="decimal"
                      className="ml-auto max-w-[120px] text-right"
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={() => void handleCreateExtra()}
                      disabled={savingExtra || Boolean(deletingExtraId)}
                    >
                      {savingExtra ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Añadir extra
                    </Button>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="flex justify-end">
            <p className="text-sm text-gray-600">
              Total extras: <span className="text-base text-gray-900">{formatCurrency(totalExtrasAmount)}</span>
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gray-50 shadow-sm">
        <CardContent className="p-6">
          <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-lg text-gray-900">Resumen Total del Subproyecto</h3>
              <p className="text-sm text-gray-600">
                {(totals?.roomsCount ?? rooms.length)} estancias • {(totals?.itemsCount ?? 0)} items
              </p>
            </div>
            <div className="grid w-full max-w-xl gap-2 text-right sm:grid-cols-2 md:grid-cols-3 md:grid-rows-4 md:justify-self-end">
              <div className="flex flex-col items-end md:col-start-1 md:row-start-1">
                <p className="text-sm text-gray-600">Coste planificado total</p>
                <p className="text-xl text-gray-900">{formatCurrency(plannedCostAmount)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-2 md:row-start-1">
                <p className="text-sm text-gray-600">Montaje total</p>
                <p className="text-xl text-gray-900">{formatCurrency(totalMountingAmount)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-3 md:row-start-1">
                <p className="text-sm text-gray-600">PVP planificado</p>
                <p className="text-xl text-gray-900">{formatCurrency(plannedBudgetAmount)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-1 md:row-start-2">
                <p className="text-sm text-gray-600">Coste real total</p>
                <p className="text-xl text-gray-900">{formatCurrency(actualCostAmount)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-2 md:row-start-2">
                <p className="text-sm text-gray-600">Extras total</p>
                <p className="text-xl text-gray-900">{formatCurrency(totalExtrasAmount)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-3 md:row-start-2">
                <p className="text-sm text-gray-600">PVP total</p>
                <p className="text-xl text-gray-900">{formatCurrency(actualBudgetAmount)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-1 md:row-start-3">
                <p className="text-sm text-gray-600">Desvio coste</p>
                <p
                  className={`text-xl ${
                    totalDesvioCosteAmount > 0
                      ? 'text-green-600'
                      : totalDesvioCosteAmount < 0
                        ? 'text-red-600'
                        : 'text-gray-900'
                  }`}
                >
                  {formatCurrency(totalDesvioCosteAmount)}
                </p>
              </div>
              <div className="flex flex-col items-end md:col-start-2 md:row-start-3">
                <p className="text-sm text-gray-600">Coste total</p>
                <p className="text-xl text-gray-900">{formatCurrency(aggregatedCost)}</p>
              </div>
              <div className="flex flex-col items-end md:col-start-3 md:row-start-3">
                <p className="text-sm text-gray-600">Planificado - real</p>
                <p
                  className={`text-xl ${
                    plannedVsRealDiff >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {formatCurrency(plannedVsRealDiff)}
                </p>
              </div>
              <div className="flex flex-col items-end md:col-start-3 md:row-start-4">
                <p className="text-sm text-gray-600">Beneficio</p>
                <p
                  className={`text-xl ${
                    benefitAmount > 0 ? 'text-green-600' : benefitAmount < 0 ? 'text-red-600' : 'text-gray-900'
                  }`}
                >
                  {formatCurrency(benefitAmount)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <Dialog
        open={pdfExportDialogOpen}
        onOpenChange={(open) => {
          if (downloadingPdf && !open) return;
          setPdfExportDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Exportar presupuesto en PDF</DialogTitle>
            <DialogDescription>
              Elige las columnas opcionales que quieres incluir. Descripción, UD, PVP y PVP+IVA
              siempre se incluyen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-md border border-gray-200 p-4">
              <p className="mb-3 text-sm font-medium text-gray-700">Columnas fijas</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {PDF_FIXED_COLUMN_LABELS.map((label) => (
                  <label key={label} className="flex items-center gap-2 text-sm text-gray-700">
                    <Checkbox checked disabled />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="rounded-md border border-gray-200 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-medium text-gray-700">Columnas opcionales</p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={toggleAllPdfOptionalColumns}
                  disabled={downloadingPdf}
                >
                  {areAllPdfOptionalColumnsSelected
                    ? 'Deseleccionar opcionales'
                    : 'Seleccionar opcionales'}
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {PDF_OPTIONAL_COLUMN_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2 text-sm text-gray-700">
                    <Checkbox
                      checked={selectedPdfOptionalColumns.includes(option.value)}
                      disabled={downloadingPdf}
                      onCheckedChange={(checked) => togglePdfOptionalColumn(option.value, Boolean(checked))}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setPdfExportDialogOpen(false)}
              disabled={downloadingPdf}
            >
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => void handleConfirmPdfExport()}
              disabled={downloadingPdf}
            >
              {downloadingPdf ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Exportar PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(notesPreview)} onOpenChange={(open) => { if (!open && !savingNote) closeNotesPreview(); }}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Notas para {notesPreview?.item.ref}</DialogTitle>
            <DialogDescription>Escribe o edita las observaciones internas del item.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Textarea
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              rows={5}
              disabled={savingNote}
              placeholder="Observaciones para el proveedor o instalador"
              wrap="soft" // muestra saltos en pantalla sin alterar el texto enviado
              className="min-h-[300px] max-h-[550px] resize-y overflow-x-hidden break-all whitespace-pre-wrap"
            />
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={closeNotesPreview} disabled={savingNote}>
              Cancelar
            </Button>
            <Button onClick={handleSaveNote} disabled={savingNote || noteDraft === notesPreview?.item.notes}>
              {savingNote ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog
  open={Boolean(roomToEdit)}
  onOpenChange={(open) => { if (!open && !renamingRoom) { setRoomToEdit(null); setRoomNameDraft(''); } }}
>
  <DialogContent>
    <DialogHeader>
      <DialogTitle>Editar estancia</DialogTitle>
      <DialogDescription>Cambia el nombre visible en todos los items.</DialogDescription>
    </DialogHeader>
    <Input
      value={roomNameDraft}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRoomNameDraft(event.target.value)}
      disabled={renamingRoom}
      placeholder="Ej. Cocina principal"
    />
    <div className="flex justify-end gap-2">
      <Button variant="outline" onClick={() => { setRoomToEdit(null); setRoomNameDraft(''); }} disabled={renamingRoom}>
        Cancelar
      </Button>
      <Button
        className="bg-blue-600 hover:bg-blue-700"
        disabled={renamingRoom || roomNameDraft.trim().length === 0}
        onClick={async () => {
          if (!roomToEdit) return;
          setRenamingRoom(true);
          try {
            await updateRoom(subprojectId, roomToEdit.id, { name: roomNameDraft.trim() });
            toast.success('Estancia renombrada');
            await refetch();
            onSubprojectUpdated?.();
            setRoomToEdit(null);
            setRoomNameDraft('');
          } catch (error) {
            toast.error('No se pudo renombrar la estancia');
          } finally {
            setRenamingRoom(false);
          }
        }}
      >
        {renamingRoom ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Guardar cambios
      </Button>
    </div>
  </DialogContent>
</Dialog>

      <Dialog
        open={catalogDialogOpen}
        onOpenChange={(open) => {
          if (!open && !catalogSubmitting) {
            cancelCatalogAddFlow();
          }
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Añadir item desde catálogo</DialogTitle>
            <DialogDescription>
              Busca por referencia, proveedor o descripción. Se copiarán los valores del catálogo y solo podrás ajustar la cantidad y la estancia antes de crear.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
              <CatalogSearchInput
                label="Referencia"
                placeholder="Ej. REF-001"
                value={searchRefInput}
                field="ref"
                items={catalogItems}
                loading={catalogLoading}
                disabled={catalogSubmitting}
                onFocus={() => {
                  setCatalogSearchField('ref');
                  setCatalogSearchValue(searchRefInput);
                }}
                onChange={(next) => {
                  setCatalogSearchField('ref');
                  setSearchRefInput(next);
                  setCatalogSearchValue(next);
                  setSelectedCatalogItem(null);
                }}
                onSelect={handleSelectCatalogItem}
              />
              <CatalogSearchInput
                label="Proveedor"
                placeholder="Marca o distribuidor"
                value={searchSupplierInput}
                field="supplier"
                items={catalogItems}
                loading={catalogLoading}
                disabled={catalogSubmitting}
                onFocus={() => {
                  setCatalogSearchField('supplier');
                  setCatalogSearchValue(searchSupplierInput);
                }}
                onChange={(next) => {
                  setCatalogSearchField('supplier');
                  setSearchSupplierInput(next);
                  setCatalogSearchValue(next);
                  setSelectedCatalogItem(null);
                }}
                onSelect={handleSelectCatalogItem}
              />
              <CatalogSearchInput
                label="Descripción"
                placeholder="Ej. Sofá 3 plazas"
                value={searchDescriptionInput}
                field="description"
                items={catalogItems}
                loading={catalogLoading}
                disabled={catalogSubmitting}
                onFocus={() => {
                  setCatalogSearchField('description');
                  setCatalogSearchValue(searchDescriptionInput);
                }}
                onChange={(next) => {
                  setCatalogSearchField('description');
                  setSearchDescriptionInput(next);
                  setCatalogSearchValue(next);
                  setSelectedCatalogItem(null);
                }}
                onSelect={handleSelectCatalogItem}
              />

            {selectedCatalogItem ? (
              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-800">
                <p className="font-semibold text-gray-900">
                  {selectedCatalogItem.ref} • {selectedCatalogItem.supplier}
                </p>
                <p className="text-gray-700">{selectedCatalogItem.description}</p>
                <p className="text-xs text-gray-600">
                  Categoría: {selectedCatalogItem.category ?? 'Sin categoría'} • Precio tarifa: {formatCurrency(parseDecimal(selectedCatalogItem.unit_cost))} • Coste: {formatCurrency(parseDecimal(selectedCatalogItem.cost_real))} • PVP: {formatCurrency(parseDecimal(selectedCatalogItem.pvp))} • Desvío comercial: {formatPercent(parseDecimal(selectedCatalogItem.desvio_comercial_pct))} • Beneficio comercial: {formatPercent(parseDecimal(selectedCatalogItem.beneficio_comercial_pct))}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Selecciona un item del catálogo para precargar sus valores.
              </p>
            )}

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Estancia</p>
                <Input
                  value={catalogRoomName}
                  onChange={(event) => setCatalogRoomName(event.target.value)}
                  list="room-suggestions"
                  placeholder="Ej. Cocina principal"
                  disabled={catalogSubmitting}
                />
                <datalist id="room-suggestions">
                  {roomOptions.map((option) => (
                    <option key={option.value} value={option.label} />
                  ))}
                </datalist>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Categoría</p>
                <CategorySearchInput
                  value={catalogCategory}
                  options={categoryOptions}
                  loading={categoriesLoading}
                  disabled={catalogSubmitting}
                  onChange={(next) => {
                    setCatalogCategoryEdited(true);
                    setCatalogCategory(next);
                    setCatalogCategoryId('');
                  }}
                  onSelect={(option) => {
                    setCatalogCategoryEdited(false);
                    setCatalogCategory(option.label);
                    setCatalogCategoryId(option.value);
                  }}
                />
              </div>
                            <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Cantidad</p>
                <Input
                  type="number"
                  min={1}
                  step={1}
                  value={catalogQuantity}
                  onChange={(event) => setCatalogQuantity(Number(event.target.value) || 0)}
                  disabled={catalogSubmitting}
                  inputMode="numeric"
                />
              </div>
            </div>


          </div>

          <div className="mt-4 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                cancelCatalogAddFlow();
              }}
              disabled={catalogSubmitting}
            >
              Cancelar
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => void handleCatalogSubmit()}
              disabled={catalogSubmitting || !selectedCatalogItem || !catalogCategoryId}
            >
              {catalogSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Añadir item
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <SubprojectDuplicateItemDialog
        open={Boolean(itemToDuplicate)}
        isSubmitting={duplicatingItem}
        sourceRoomId={itemToDuplicate?.roomId ?? ''}
        sourceRoomName={itemToDuplicate?.roomName ?? ''}
        sourceCategoryId={itemToDuplicate?.item.category_id ?? ''}
        sourceCategoryName={itemToDuplicate?.item.category}
        itemRef={itemToDuplicate?.item.ref ?? ''}
        roomOptions={duplicateRoomOptions}
        categoryOptions={duplicateCategoryOptions}
        noRoomOptionsMessage={duplicateNoRoomOptionsMessage}
        onClose={closeDuplicateItemDialog}
        onSubmit={handleDuplicateItemSubmit}
      />

      <ItemFormDialog
        open={itemDialogOpen}
        initialValues={itemFormInitialValues}
        isSubmitting={itemSubmitting}
        onClose={closeItemDialog}
        onSubmit={handleItemSubmit}
        roomOptions={roomOptions}
        categoryOptions={categoryOptions}
        categoriesLoading={categoriesLoading}
        supplierOptions={supplierOptions}
        suppliersLoading={suppliersLoading}
      />
      <SubprojectCreateCatalogItemDialog
        open={createCatalogItemDialogOpen}
        initialValues={createCatalogItemInitialValues}
        isSubmitting={itemSubmitting}
        onClose={closeCreateCatalogItemDialog}
        onSubmit={handleCreateCatalogItemSubmit}
        roomOptions={roomOptions}
        categoryOptions={categoryOptions}
        categoryLoading={categoriesLoading}
        supplierOptions={supplierOptions}
        supplierLoading={suppliersLoading}
      />
      <AlertDialog
        open={Boolean(itemToDelete)}
        onOpenChange={(open) => {
          if (!open && !deletingItemId) {
            setItemToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar item?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el item de la estancia seleccionada. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingItemId)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={Boolean(deletingItemId)}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteItem();
              }}
            >
              {deletingItemId ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={Boolean(extraToDelete)}
        onOpenChange={(open) => {
          if (!open && !deletingExtraId) {
            setExtraToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar extra?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la línea de extra seleccionada. No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingExtraId)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={Boolean(deletingExtraId)}
              onClick={(event) => {
                event.preventDefault();
                void handleDeleteExtra();
              }}
            >
              {deletingExtraId ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SubprojectOrdersDialog
  open={orderDialogOpen}
  onClose={() => setOrderDialogOpen(false)}
  projectId={projectId}
  subprojectId={subprojectId}
  rooms={rooms}
  onCreated={() => { void refetch(); onSubprojectUpdated?.(); }}
/>
    </div>
  );
}
